'use client'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import FlightLandIcon from '@mui/icons-material/FlightLand'
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff'
import SwapVertIcon from '@mui/icons-material/SwapVert'
import { Box, Button, Divider, IconButton, Stack, SwipeableDrawer, Tab, Tabs, Typography, alpha } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

type SearchTripType = 'oneway' | 'roundtrip' | 'multicity'

const modifierCardSx = {
    border: 1,
    borderColor: 'grey.200',
    borderRadius: 2,
    bgcolor: 'background.paper',
    overflow: 'hidden'
}

const fieldRowSx = {
    px: 1.5,
    py: 1.25,
    cursor: 'pointer',
    '&:hover': { bgcolor: 'action.hover' }
}

interface SearchHeaderProps {
    open: boolean
    onOpen: () => void
    onClose: () => void
    tripType: SearchTripType
    onTripTypeChange: (type: SearchTripType) => void
    multicityContent?: React.ReactNode
    fromLocation: string
    toLocation: string
    departDate: string
    returnDate?: string
    travelers: string
    flightClass: string
    onFromClick: () => void
    onToClick: () => void
    onDateClick: () => void
    onTravelersClick: () => void
    onSearch: () => void
    onSwap: () => void
    hasError?: boolean
    srpResultsLoading?: boolean
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
    open,
    onOpen,
    onClose,
    tripType,
    onTripTypeChange,
    multicityContent,
    fromLocation,
    toLocation,
    departDate,
    returnDate,
    travelers,
    flightClass,
    onFromClick,
    onToClick,
    onDateClick,
    onTravelersClick,
    onSearch,
    onSwap,
    hasError,
    srpResultsLoading = false
}) => {
    const t = useTranslations('common')

    const isMulticity = tripType === 'multicity'

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        onTripTypeChange(newValue as SearchTripType)
    }

    return (
        <SwipeableDrawer
            anchor="top"
            open={open}
            onClose={onClose}
            onOpen={onOpen}
            disableSwipeToOpen={false}
            PaperProps={{
                sx: {
                    borderBottomLeftRadius: 16,
                    borderBottomRightRadius: 16,
                    height: 'auto',
                    minHeight: '470px'
                }
            }}
        >
            <Box sx={{ px: 2 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5,
                        height: '56px'
                    }}
                >
                    <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>{t('updateSearch')}</Typography>
                </Box>

                {/* Trip type tabs + form body inside one card */}
                <Box sx={{ ...modifierCardSx, mb: 2 }}>
                    <Tabs
                        value={tripType}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            minHeight: 44,
                            height: 44,
                            px: 1.5,
                            borderBottom: 1,
                            borderColor: 'grey.200',
                            '& .MuiTabs-indicator': {
                                display: 'none'
                            },
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: 14,
                                fontWeight: 500,
                                minHeight: 44,
                                height: 44,
                                color: 'text.secondary',
                                mx: 0.5,
                                transition: 'color 0.2s',
                                '&.Mui-selected': {
                                    color: theme => alpha(theme.palette.link.main, 0.9),
                                    fontWeight: 600,
                                    borderBottom: 2,
                                    borderColor: theme => alpha(theme.palette.link.main, 0.9)
                                }
                            }
                        }}
                    >
                        <Tab label={t('oneWay')} value="oneway" />
                        <Tab label={t('roundTrip')} value="roundtrip" />
                        <Tab label={t('multicity')} value="multicity" />
                    </Tabs>

                    {/* Keep both trip-type UIs mounted; hide inactive branch with CSS so
                        switching tabs does not remount components that hold RTK subscriptions. */}
                    <Box sx={{ display: isMulticity ? 'none' : 'block', pointerEvents: isMulticity ? 'none' : 'auto' }}>
                        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ ...modifierCardSx, borderColor: 'grey.200' }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Box onClick={onFromClick} sx={{ ...fieldRowSx, pr: 6 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <FlightTakeoffIcon color="action" sx={{ fontSize: 20 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                    {t('fromAirportPlaceholder')}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: hasError ? 'error.main' : 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {fromLocation}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    <Divider />

                                    <Box onClick={onToClick} sx={{ ...fieldRowSx, pr: 6 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <FlightLandIcon color="action" sx={{ fontSize: 20 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                    {t('toAirportPlaceholder')}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        color: hasError ? 'error.main' : 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {toLocation}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            right: 12,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 2
                                        }}
                                    >
                                        <IconButton
                                            onClick={onSwap}
                                            size="small"
                                            sx={{
                                                bgcolor: 'background.paper',
                                                border: 1,
                                                borderColor: 'grey.200',
                                                width: 32,
                                                height: 32,
                                                boxShadow: 1
                                            }}
                                        >
                                            <SwapVertIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                        </IconButton>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box onClick={onDateClick} sx={fieldRowSx}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <CalendarMonthIcon color="action" sx={{ fontSize: 20 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                {tripType === 'roundtrip'
                                                    ? `${t('departure')} - ${t('return')}`
                                                    : t('departure')}
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                                {tripType === 'roundtrip' && returnDate
                                                    ? `${departDate} - ${returnDate}`
                                                    : departDate}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Box>

                            <Box onClick={onTravelersClick} sx={{ ...modifierCardSx, ...fieldRowSx, borderColor: 'grey.200' }}>
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
                                    {t('travellerClassHeading')}
                                </Typography>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                    {travelers} &bull; {t(flightClass)}
                                </Typography>
                            </Box>

                            <Button
                                disabled={srpResultsLoading}
                                fullWidth
                                variant="customVariantPrimary"
                                size="large"
                                onClick={onSearch}
                                sx={{
                                    height: 40,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: 16
                                }}
                            >
                                {t('modifySearch')}
                            </Button>
                        </Box>
                    </Box>
                    <Box sx={{ display: isMulticity ? 'block' : 'none', pointerEvents: isMulticity ? 'auto' : 'none' }}>
                        {multicityContent}
                    </Box>
                </Box>

                {/* Handle */}
                <Box
                    sx={{
                        width: '40px',
                        height: '4px',
                        bgcolor: 'grey.500',
                        borderRadius: '2px',
                        mx: 'auto',
                        mt: 1.5
                    }}
                />
            </Box>
        </SwipeableDrawer>
    )
}

export { SearchHeader }
export type { SearchHeaderProps, SearchTripType }
